// What the read-only Tiptap instance used to do to stored article HTML, as a
// string pass.
//
// Until 2026-09 the article body was rendered client-side: `useEditor({ content })`
// parsed the stored HTML through the Tiptap schema, `getHTML()` serialised it
// back, and that string was set as innerHTML. The schema round-trip was never
// meant as a formatter, but after years of content it *is* one — it decided how
// pasted spans, image classes, table widths and link attributes ended up on the
// page. Rendering the stored HTML directly on the server (so the body is in the
// first response and the ~120 KB editor bundle stops shipping to readers) has to
// reproduce those decisions or older articles change appearance. Each rule below
// names the Tiptap extension whose behaviour it mirrors; the read path had exactly
// these extensions (Document, Paragraph, Text, Heading 1-4, Bold, Italic,
// Underline, TextAlign on heading+paragraph, Link, ListItem, BulletList,
// OrderedList, Image(inline, size/float), GlobalClass, Iframe, Blockquote,
// HorizontalRule, Table(+borderless), TableCell, TableHeader, TableRow, Color,
// TextStyle). The equivalence is checked against every published article by
// scripts/article-body-harness, which runs the real extensions in Chrome next to
// this function and compares the rendered trees (effective marks per text run,
// so `<strong><span>` and `<span><strong>` count as the same thing — they are).
//
// The pass also does the sanitising the schema did as a side effect: no scripts,
// no inline handlers, no javascript: URLs survive.

const getAttr = (attrs: string, name: string): string | null => {
  const m = attrs.match(new RegExp(`(?:^|\\s)${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s"'>]+))`, "i"));
  if (!m) return null;
  return m[1] ?? m[2] ?? m[3] ?? "";
};

const escapeAttr = (value: string): string =>
  value.replace(/&(?!(?:amp|lt|gt|quot|#39|#x27|nbsp|#\d+|#x[\da-f]+);)/gi, "&amp;").replace(/"/g, "&quot;");

const attr = (name: string, value: string | null | undefined): string =>
  value === null || value === undefined ? "" : ` ${name}="${escapeAttr(value)}"`;

const styleDeclarations = (style: string | null): { prop: string; value: string }[] =>
  (style || "")
    .split(";")
    .map((d) => d.trim())
    .filter(Boolean)
    .map((d) => {
      const i = d.indexOf(":");
      return i === -1 ? null : { prop: d.slice(0, i).trim().toLowerCase(), value: d.slice(i + 1).trim() };
    })
    .filter((d): d is { prop: string; value: string } => !!d);

// A `style="…"` value reduced to the declarations the schema kept.
const pickStyle = (style: string | null, keep: string[]): string | null => {
  const kept = styleDeclarations(style)
    .filter((d) => keep.includes(d.prop))
    .map((d) => `${d.prop}: ${d.value}`);
  return kept.length ? kept.join("; ") : null;
};

// TextAlign: only its four alignments are attributes; `start`, `end`, `inherit`
// and friends fall back to the (null) default and render nothing.
const TEXT_ALIGNS = ["left", "center", "right", "justify"];
const textAlignStyle = (style: string | null): string | null => {
  const d = styleDeclarations(style).find((x) => x.prop === "text-align");
  return d && TEXT_ALIGNS.includes(d.value.toLowerCase()) ? `text-align: ${d.value.toLowerCase()}` : null;
};

// Elements the schema had no node or mark for: their children were kept, the
// wrapper was not (ProseMirror DOMParser `addAll`). Whether the unwrapped tag was
// one of ProseMirror's block tags decides if it split the surrounding inline
// content into separate paragraphs.
const PM_BLOCK_TAGS = new Set([
  "address", "article", "aside", "blockquote", "canvas", "dd", "div", "dl", "fieldset", "figcaption", "figure",
  "footer", "form", "h1", "h2", "h3", "h4", "h5", "h6", "header", "hgroup", "hr", "li", "noscript", "ol",
  "output", "p", "pre", "section", "table", "tfoot", "ul",
]);

// Leaf elements with no rule were dropped outright, content and all.
const DROP = new Set([
  "br", "video", "audio", "source", "track", "embed", "object", "param", "input", "button", "svg", "canvas",
  "picture", "wbr", "select", "textarea", "meta", "link", "base", "col", "colgroup", "caption",
]);

const VOID = new Set(["img", "hr", "br", "col", "wbr", "input", "meta", "link", "base", "source", "track", "embed", "param"]);

// Block nodes this pass emits, and which of them hold *blocks* (so bare inline
// content inside them gets a paragraph — Paragraph is the only textblock besides
// Heading, and Blockquote/ListItem/TableCell/TableHeader all have `block+`
// content in this schema).
const EMITTED_BLOCKS = new Set(["p", "h1", "h2", "h3", "h4", "ul", "ol", "li", "blockquote", "table", "tr", "td", "th", "iframe"]);
const WRAPS_INLINE_IN_P = new Set(["root", "blockquote", "li", "td", "th"]);
// Structural containers whose direct text content the schema has no place for.
const NO_INLINE = new Set(["ul", "ol", "table", "tr"]);

type Token = { kind: "text"; text: string } | { kind: "open" | "close"; tag: string; attrs: string; raw: string };

const tokenize = (html: string): Token[] => {
  const tokens: Token[] = [];
  const re = /<(\/?)([a-zA-Z][\w:-]*)((?:\s+[^\s=>\/]+(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'>]+))?)*)\s*\/?>/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html))) {
    if (m.index > last) tokens.push({ kind: "text", text: html.slice(last, m.index) });
    tokens.push({ kind: m[1] ? "close" : "open", tag: m[2].toLowerCase(), attrs: m[3] || "", raw: m[0] });
    last = m.index + m[0].length;
  }
  if (last < html.length) tokens.push({ kind: "text", text: html.slice(last) });
  return tokens;
};

const stripDangerous = (html: string): string =>
  html
    .replace(/<!--[\s\S]*?-->/g, "")
    .replace(/<(script|style|noscript|title|head|template|object)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, "")
    .replace(/<(script|style)\b[^>]*\/?>/gi, "");

const safeUrl = (value: string | null): string | null => {
  if (value === null) return null;
  const v = value.trim();
  if (/^\s*(javascript|vbscript|data:text\/html)/i.test(v)) return "#";
  return v;
};

// @tiptap/extension-link isAllowedUri (default protocols), reproduced as it
// actually runs, not as it reads: the extension builds its pattern from a
// template string in which `\-` loses the backslash, so the trailing class is
// `[^a-z+.-:]` — a *range* from `.` to `:` that also excludes `/` and the
// digits. Hence `example.com` is a link but `example.com/x` is not (the text
// stays), while anything with a listed scheme, or starting with a non-letter
// (`/path`, `#anchor`, `//host`), passes. Every published article agrees with
// this reading (scripts/article-body-harness). Whitespace is stripped first.
const LINK_WHITESPACE_RE = /[\u0000-\u0020\u00A0\u1680\u180E\u2000-\u2029\u205F\u3000]/g;
const LINK_URI_RE = /^(?:(?:http|https|ftp|ftps|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z0-9+.-]+(?:[^a-z+\x2E-\x3A]|$))/i;
const isAllowedLinkUri = (href: string): boolean => {
  if (/^\s*(javascript|vbscript|data):/i.test(href)) return false;
  return LINK_URI_RE.test(href.replace(LINK_WHITESPACE_RE, ""));
};

// Table + TableView.updateColumns: the <colgroup> is rebuilt from the first row's
// cells (any colgroup in the source is ignored by the parser), one <col> per
// spanned column, `width: Npx` when the cell stored a colwidth for it and
// `min-width: 25px` otherwise; the table gets `width` when every column is fixed,
// else `min-width`, both as the sum. 25 is the extension's default cellMinWidth.
const CELL_MIN_WIDTH = 25;
const buildColgroup = (tableInner: string): { colgroup: string; tableStyle: string } => {
  const firstRow = tableInner.match(/<tr\b[^>]*>([\s\S]*?)<\/tr\s*>/i);
  const cells = firstRow ? Array.from(firstRow[1].matchAll(/<(td|th)\b([^>]*)>/gi)) : [];
  let total = 0;
  let fixed = true;
  const cols: string[] = [];
  for (const cell of cells) {
    const colspan = Math.max(1, parseInt(getAttr(cell[2], "colspan") || "1", 10) || 1);
    const widths = (getAttr(cell[2], "colwidth") || "").split(",").map((w) => parseInt(w, 10));
    for (let j = 0; j < colspan; j++) {
      const w = widths[j];
      if (w && !Number.isNaN(w)) {
        cols.push(`<col style="width: ${Math.max(w, CELL_MIN_WIDTH)}px">`);
        total += w;
      } else {
        cols.push(`<col style="min-width: ${CELL_MIN_WIDTH}px">`);
        total += CELL_MIN_WIDTH;
        fixed = false;
      }
    }
  }
  return { colgroup: `<colgroup>${cols.join("")}</colgroup>`, tableStyle: `${fixed ? "width" : "min-width"}: ${total}px` };
};

type Emit = { open: string; close: string; block: boolean; inline: boolean };

// The rewrite of one opening tag. `open`/`close` are what gets written for the
// pair (both empty for an unwrapped tag), `block` marks emitted block nodes,
// `inline` marks inline content that needs a paragraph around it at block level.
const rewriteOpen = (tag: string, attrs: string, tableInner: string | null): Emit | "drop" | "unwrap" => {
  switch (tag) {
    // Image (inline) + the size/float extension: class is *replaced* with the
    // two custom-image classes, alt defaults to "", size/float are always written
    // (rendered: true). width/height/style/srcset from paste are not attributes
    // of the node and were dropped.
    case "img": {
      const src = safeUrl(getAttr(attrs, "src"));
      if (src === null) return "drop"; // parse rule was img[src] — an empty src still matches
      const size = getAttr(attrs, "size") || "small";
      const float = getAttr(attrs, "float") || "none";
      return {
        open:
          `<img${attr("src", src)}${attr("alt", getAttr(attrs, "alt") ?? "")}${attr("title", getAttr(attrs, "title"))}` +
          `${attr("size", size)}${attr("float", float)}${attr("class", ` custom-image-${size} custom-image-float-${float}`)}>`,
        close: "",
        block: false,
        inline: true,
      };
    }
    // Link: href/target/rel/class, with the extension's defaults filling the gaps.
    // A href the extension's isAllowedUri rejects is not a link at all (the text
    // stays), which is also the XSS guard.
    case "a": {
      const href = getAttr(attrs, "href");
      if (href === null || !isAllowedLinkUri(href)) return "unwrap";
      return {
        open:
          `<a${attr("target", getAttr(attrs, "target") ?? "_blank")}${attr("rel", getAttr(attrs, "rel") ?? "noopener noreferrer nofollow")}` +
          `${attr("class", getAttr(attrs, "class"))}${attr("href", href)}>`,
        close: "</a>",
        block: false,
        inline: true,
      };
    }
    // TextStyle keeps a span only when it carries a style attribute; Color is the
    // one declaration that survives. Plain spans are unwrapped.
    case "span": {
      const style = getAttr(attrs, "style");
      if (style === null) return "unwrap";
      return { open: `<span${attr("style", pickStyle(style, ["color"]))}>`, close: "</span>", block: false, inline: true };
    }
    case "b":
    case "strong":
      return { open: "<strong>", close: "</strong>", block: false, inline: true };
    case "i":
    case "em":
      return { open: "<em>", close: "</em>", block: false, inline: true };
    case "u":
      return { open: "<u>", close: "</u>", block: false, inline: true };
    // Paragraph: GlobalClass keeps `class`; TextAlign keeps a valid text-align.
    case "p":
      return { open: `<p${attr("class", getAttr(attrs, "class"))}${attr("style", textAlignStyle(getAttr(attrs, "style")))}>`, close: "</p>", block: true, inline: false };
    case "h1":
    case "h2":
    case "h3":
    case "h4":
      return { open: `<${tag}${attr("style", textAlignStyle(getAttr(attrs, "style")))}>`, close: `</${tag}>`, block: true, inline: false };
    case "ul":
      return { open: `<ul${attr("class", getAttr(attrs, "class"))}>`, close: "</ul>", block: true, inline: false };
    case "hr":
      return { open: `<hr${attr("class", getAttr(attrs, "class"))}>`, close: "", block: true, inline: false };
    // OrderedList: `start` is the one attribute, written only when it is not 1.
    case "ol": {
      const start = parseInt(getAttr(attrs, "start") || "1", 10);
      return { open: `<ol${Number.isInteger(start) && start !== 1 ? ` start="${start}"` : ""}>`, close: "</ol>", block: true, inline: false };
    }
    case "li":
    case "blockquote":
    case "tr":
      return { open: `<${tag}>`, close: `</${tag}>`, block: true, inline: false };
    case "td":
    case "th": {
      const colspan = parseInt(getAttr(attrs, "colspan") || "1", 10) || 1;
      const rowspan = parseInt(getAttr(attrs, "rowspan") || "1", 10) || 1;
      const colwidth = getAttr(attrs, "colwidth");
      return {
        open: `<${tag} colspan="${colspan}" rowspan="${rowspan}"${attr("colwidth", colwidth)}>`,
        close: `</${tag}>`,
        block: true,
        inline: false,
      };
    }
    case "table": {
      const { colgroup, tableStyle } = buildColgroup(tableInner || "");
      return {
        open: `<table${getAttr(attrs, "data-borderless") === "true" ? ' data-borderless="true"' : ""} style="${tableStyle}">${colgroup}<tbody>`,
        close: "</tbody></table>",
        block: true,
        inline: false,
      };
    }
    // The parser has no rule for tbody/thead/tfoot; rows are collected directly
    // and the serialiser always writes one tbody (emitted with <table> above).
    case "tbody":
    case "thead":
    case "tfoot":
      return "unwrap";
    // Iframe node: src/frameborder/allowfullscreen, rendered inside the 16:9 box.
    // The wrapper div in the source is unwrapped like any div, so re-wrapping
    // here is what keeps the output stable.
    case "iframe": {
      const src = safeUrl(getAttr(attrs, "src"));
      return {
        open: `<div class="aspect-w-16 aspect-h-9"><iframe${attr("src", src)}${attr("frameborder", getAttr(attrs, "frameborder") ?? "0")} allowfullscreen="true">`,
        close: "</iframe></div>",
        block: true,
        inline: false,
      };
    }
    default:
      if (DROP.has(tag)) return "drop";
      return "unwrap";
  }
};

/**
 * Stored article HTML → what Tiptap's read-only round-trip produced from it.
 * Idempotent: running it over its own output changes nothing.
 */
export const normalizeStoredArticleHtml = (html: string): string => {
  const clean = stripDangerous(html);
  const tokens = tokenize(clean);
  const out: string[] = [];

  // Open source tags → what closes them. `emit` is null for unwrapped/dropped
  // tags so their close tag is swallowed too.
  const source: { tag: string; emit: Emit | null; unwrappedBlock: boolean }[] = [];
  // Open *emitted* block nodes, plus "p*" for a paragraph this pass opened itself.
  const blocks: string[] = [];
  // Depth inside an element whose whole subtree is dropped.
  let dropDepth = 0;

  const container = () => (blocks.length ? blocks[blocks.length - 1].replace("*", "") : "root");
  const closeAutoParagraph = () => {
    if (blocks.length && blocks[blocks.length - 1] === "p*") {
      blocks.pop();
      out.push("</p>");
    }
  };
  const ensureParagraph = () => {
    if (WRAPS_INLINE_IN_P.has(container())) {
      blocks.push("p*");
      out.push("<p>");
    }
  };

  // Precomputed <table>…</table> inner HTML per opening-table token index, for
  // the colgroup rebuild (tables never nest in this schema).
  const tableInnerAt = new Map<number, string>();
  {
    let start = -1;
    let acc: string[] = [];
    tokens.forEach((t, i) => {
      if (t.kind === "open" && t.tag === "table" && start === -1) {
        start = i;
        acc = [];
        return;
      }
      if (start !== -1) {
        if (t.kind === "close" && t.tag === "table") {
          tableInnerAt.set(start, acc.join(""));
          start = -1;
        } else acc.push(t.kind === "text" ? t.text : t.raw);
      }
    });
  }

  tokens.forEach((t, index) => {
    if (dropDepth > 0) {
      if (t.kind === "open" && !VOID.has(t.tag)) dropDepth++;
      else if (t.kind === "close") dropDepth--;
      return;
    }

    if (t.kind === "text") {
      const c = container();
      if (t.text.trim() === "" && (c === "root" || WRAPS_INLINE_IN_P.has(c) || NO_INLINE.has(c))) return; // whitespace between blocks
      if (NO_INLINE.has(c)) return; // text directly inside ul/ol/table/tr has no node
      if (WRAPS_INLINE_IN_P.has(c)) ensureParagraph();
      out.push(t.text);
      return;
    }

    if (t.kind === "open") {
      const r = rewriteOpen(t.tag, t.attrs, tableInnerAt.get(index) ?? null);
      if (r === "drop") {
        if (!VOID.has(t.tag)) dropDepth = 1;
        return;
      }
      if (r === "unwrap") {
        const isBlock = PM_BLOCK_TAGS.has(t.tag);
        if (isBlock) closeAutoParagraph();
        if (!VOID.has(t.tag)) source.push({ tag: t.tag, emit: null, unwrappedBlock: isBlock });
        return;
      }
      if (r.block) {
        closeAutoParagraph();
        out.push(r.open);
        if (r.close) blocks.push(t.tag);
        if (!VOID.has(t.tag)) source.push({ tag: t.tag, emit: r, unwrappedBlock: false });
        return;
      }
      // inline
      const c = container();
      if (NO_INLINE.has(c)) {
        // no home for inline content here; treat as unwrapped
        if (!VOID.has(t.tag)) source.push({ tag: t.tag, emit: null, unwrappedBlock: false });
        return;
      }
      if (WRAPS_INLINE_IN_P.has(c)) ensureParagraph();
      out.push(r.open);
      if (!VOID.has(t.tag)) source.push({ tag: t.tag, emit: r, unwrappedBlock: false });
      return;
    }

    // close tag: match the nearest open source tag of the same name
    let idx = -1;
    for (let i = source.length - 1; i >= 0; i--) {
      if (source[i].tag === t.tag) {
        idx = i;
        break;
      }
    }
    if (idx === -1) return;
    const popped = source.splice(idx);
    // Anything opened after it is implicitly closed first.
    for (let i = popped.length - 1; i >= 0; i--) {
      const s = popped[i];
      if (s.emit && s.emit.block) {
        closeAutoParagraph();
        out.push(s.emit.close);
        blocks.pop();
      } else if (s.emit) {
        out.push(s.emit.close);
      } else if (s.unwrappedBlock) {
        closeAutoParagraph();
      }
    }
  });

  // Unbalanced source: close whatever is still open, innermost first.
  for (let i = source.length - 1; i >= 0; i--) {
    const s = source[i];
    if (s.emit && s.emit.block) {
      closeAutoParagraph();
      out.push(s.emit.close);
      blocks.pop();
    } else if (s.emit) out.push(s.emit.close);
    else if (s.unwrappedBlock) closeAutoParagraph();
  }
  closeAutoParagraph();
  return out.join("");
};
